import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { CheckCircleIcon, RefreshIcon } from '@heroicons/react/outline';
import { Button } from 'components/Button';
import { withNotification } from 'components/Notification';
import { useStore } from 'context/store-context';
import { apiErrorHandler, NotificationTypes, SizeEnum } from 'utils';
import { GET_KLAVIYO_CONNECTION } from 'gql/queries';
import { DISCONNECT_KLAVIYO, GET_KLAVIYO_INSTALL_URL } from 'gql/mutations';

// All entry points share the store connection; templates and flow controls live in Notifications.
export const KlaviyoIntegration = withNotification(
  ({ showNotification }: any) => {
    const { storeId } = useStore();
    const [searchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();
    const lifecycle = useRef({ storeId, generation: 0, mounted: false });
    useLayoutEffect(() => {
      const currentLifecycle = lifecycle.current;
      currentLifecycle.storeId = storeId;
      currentLifecycle.mounted = true;
      return () => {
        currentLifecycle.mounted = false;
        currentLifecycle.generation += 1;
      };
    }, [storeId]);
    const isCurrentRequest = (request: {
      storeId: typeof storeId;
      generation: number;
    }) =>
      lifecycle.current.mounted &&
      lifecycle.current.storeId === request.storeId &&
      lifecycle.current.generation === request.generation;

    const {
      client: connectionClient,
      loading: connectionLoading,
      error: connectionError,
      refetch: refetchConnection,
    } = useQuery(GET_KLAVIYO_CONNECTION, {
      variables: { storeId },
      skip: !storeId,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      onError(error) {
        const newError = apiErrorHandler(error);
        showNotification(
          NotificationTypes.error,
          newError?.message ||
            'Could not load Klaviyo connection status, please refresh and try again.',
        );
      },
    });

    // Read the exact store entry; InMemoryCache returns null for uncached/incomplete data.
    // A previous query result must never supply another store's connected account.
    const connection = storeId
      ? connectionClient.readQuery({
          query: GET_KLAVIYO_CONNECTION,
          variables: { storeId },
        })?.klaviyoConnection
      : undefined;
    const isConnected = Boolean(
      storeId && !connectionError && connection?.connected,
    );

    const [getInstallUrl, { loading: installUrlLoading }] = useMutation(
      GET_KLAVIYO_INSTALL_URL,
    );

    const [disconnectKlaviyo, { loading: disconnectLoading }] =
      useMutation(DISCONNECT_KLAVIYO);

    useEffect(() => {
      const klaviyoParam = searchParams.get('klaviyo');
      if (!klaviyoParam || !storeId) return;

      if (klaviyoParam === 'connected') {
        showNotification(
          NotificationTypes.success,
          'Successfully connected to Klaviyo.',
        );
        refetchConnection().catch(() => {});
      } else if (klaviyoParam === 'denied') {
        showNotification(
          NotificationTypes.pending,
          "You didn't grant ShipAid access to Klaviyo, so nothing was connected. Click Connect to Klaviyo again if you'd like to link your account.",
        );
      } else if (klaviyoParam === 'expired') {
        showNotification(
          NotificationTypes.pending,
          'Your Klaviyo connection session is no longer valid. Click Connect to Klaviyo to try again.',
        );
      } else if (klaviyoParam === 'error') {
        showNotification(
          NotificationTypes.error,
          'Something went wrong connecting to Klaviyo, please try again.',
        );
      }

      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('klaviyo');
      const query = nextParams.toString();
      navigate(
        {
          pathname: location.pathname,
          search: query ? `?${query}` : '',
          hash: location.hash,
        },
        { replace: true },
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, storeId]);

    const handleConnect = () => {
      if (!storeId || installUrlLoading) return;
      const request = { storeId, generation: lifecycle.current.generation };
      getInstallUrl({
        variables: { storeId },
        onCompleted(data) {
          if (!isCurrentRequest(request)) return;
          const url = data?.klaviyoInstallUrl;
          if (url) {
            window.location.href = url;
          } else {
            showNotification(
              NotificationTypes.error,
              'Something went wrong connecting to Klaviyo, please try again.',
            );
          }
        },
        onError(error) {
          if (!isCurrentRequest(request)) return;
          const newError = apiErrorHandler(error);
          showNotification(
            NotificationTypes.error,
            newError?.message ||
              'Something went wrong connecting to Klaviyo, please try again.',
          );
        },
      });
    };

    const handleDisconnect = () => {
      if (!storeId || disconnectLoading || connectionLoading) return;
      const request = { storeId, generation: lifecycle.current.generation };
      disconnectKlaviyo({
        variables: { storeId },
        onCompleted(data) {
          if (!isCurrentRequest(request)) return;
          if (!data?.disconnectKlaviyo) {
            showNotification(
              NotificationTypes.error,
              'Could not disconnect Klaviyo. Please try again.',
            );
            return;
          }
          showNotification(
            NotificationTypes.success,
            'Disconnected from Klaviyo.',
          );
          refetchConnection({ storeId: request.storeId }).catch(() => {});
        },
        onError(error) {
          if (!isCurrentRequest(request)) return;
          const newError = apiErrorHandler(error);
          showNotification(
            NotificationTypes.error,
            newError?.message ||
              'Something went wrong disconnecting from Klaviyo, please try again.',
          );
        },
      });
    };

    return (
      <section className="overflow-hidden rounded-xl border border-[#d8dfeb] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.05)]">
        <div className="flex flex-wrap items-center justify-between gap-4 p-6">
          <div className="flex min-w-0 items-center gap-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#eef5ff] text-[#2563eb]">
              <RefreshIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold leading-snug text-[#0b1c30]">
                Klaviyo Integration
              </h2>
              <p className="mt-1 text-sm leading-6 text-[#64748b]">
                Connect shipment and supported return and exchange notifications
                to one Klaviyo account.
              </p>
            </div>
          </div>

          {connectionLoading && !isConnected ? (
            <div
              style={{
                borderColor: 'rgba(37,99,235, 0.2)',
                borderTopColor: '#2564eb',
              }}
              className="inline-block h-6 w-6 flex-shrink-0 animate-spin rounded-full border-4"
              role="status"
              aria-label="Loading Klaviyo connection status"
            />
          ) : isConnected ? (
            <div className="flex min-w-0 max-w-full flex-wrap items-center gap-4">
              <div className="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-[#bbf0cc] bg-[#eafbf0] px-3 py-1">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-[#1ebd53]" />
                <span className="min-w-0 break-words text-xs font-semibold text-[#15803d]">
                  Connected
                  {connection?.accountName
                    ? ` · ${connection.accountName}`
                    : ''}
                </span>
              </div>
              <button
                type="button"
                onClick={handleDisconnect}
                disabled={disconnectLoading || connectionLoading}
                className="rounded-lg border border-[#fecaca] bg-white px-3 py-1.5 text-sm font-semibold text-[#b91c1c] transition hover:bg-[#fef2f2] disabled:opacity-50"
              >
                {disconnectLoading ? 'Disconnecting…' : 'Disconnect'}
              </button>
            </div>
          ) : null}
        </div>

        {!connectionLoading && connectionError ? (
          <div role="alert" className="space-y-3 border-t border-[#e5ebf4] p-6">
            <p className="text-sm text-[#b91c1c]">
              Could not load Klaviyo connection status. Try again to check your
              connection.
            </p>
            <Button
              onClick={() => {
                refetchConnection().catch(() => {});
              }}
              size={SizeEnum.md}
              variant="outlined"
            >
              Retry
            </Button>
          </div>
        ) : null}

        {isConnected ? (
          <div className="space-y-4 border-t border-[#e5ebf4] p-6">
            <p className="text-sm leading-6 text-[#64748b]">
              Manage templates, import flows, and choose which Klaviyo emails
              are live in Notifications.
            </p>
            <nav
              aria-label="Klaviyo notification setup"
              className="flex flex-wrap gap-3"
            >
              <Link
                to="/settings/notifications?tab=customer"
                className="rounded-lg border border-[#d8dfeb] px-4 py-2 text-sm font-semibold text-[#2563eb] hover:bg-[#eff4ff]"
              >
                Manage outbound emails
              </Link>
              <Link
                to="/settings/notifications?tab=returns"
                className="rounded-lg border border-[#d8dfeb] px-4 py-2 text-sm font-semibold text-[#2563eb] hover:bg-[#eff4ff]"
              >
                Manage return and exchange emails
              </Link>
            </nav>
          </div>
        ) : null}

        {!connectionLoading && !connectionError && !isConnected ? (
          <div className="space-y-4 border-t border-[#e5ebf4] p-6">
            <div className="rounded-lg border border-dashed border-[#c3cce0] bg-[#eff4ff] p-4">
              <div className="flex items-center justify-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white p-2 shadow-sm ring-1 ring-[#d8dfeb]">
                  <img
                    src="/ShipAid.svg"
                    alt="ShipAid"
                    className="h-6 w-6 object-contain"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#9aa7bd]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#b8c2d1]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-[#d3dbe8]" />
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white p-2 shadow-sm ring-1 ring-[#d8dfeb]">
                  <img
                    src="https://www.klaviyo.com/icons/icon-192x192.png"
                    alt="Klaviyo"
                    className="h-6 w-6 object-contain"
                  />
                </div>
              </div>
            </div>
            <Button
              onClick={handleConnect}
              size={SizeEnum.md}
              color="primary"
              variant="contained"
              className="w-full justify-center rounded-lg"
              disabled={!storeId || installUrlLoading}
              isLoading={installUrlLoading}
            >
              Connect Klaviyo
            </Button>
          </div>
        ) : null}
      </section>
    );
  },
);
